import { useState, useCallback } from 'react';
import { validateLink, validateDates, validatePromotionForm } from '../utils/validationUtils';

/**
 * Хук для управления валидацией формы акции
 * @param {string} mode - Режим: 'create', 'edit', 'copy'
 * @returns {Object} - Объект с состояниями ошибок и функциями валидации
 */
export const useFormValidation = (mode = 'create') => {
  const [buttonErrors, setButtonErrors] = useState({});
  const [dateErrors, setDateErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});

  /**
   * Валидация ссылки кнопки
   */
  const validateButtonLink = useCallback((index, link, isExternal) => {
    const linkError = validateLink(link, isExternal);

    setButtonErrors((prevErrors) => ({
      ...prevErrors,
      [`${index}_link`]: linkError
    }));

    return linkError;
  }, []);

  /**
   * Валидация дат
   */
  const validateFormDates = useCallback((startDate, endDate) => {
    if (mode === 'edit') {
      return {}; // В режиме редактирования пропускаем валидацию дат
    }

    const errors = validateDates(startDate, endDate);
    setDateErrors(errors);
    return errors;
  }, [mode]);

  /**
   * Удаление ошибок валидации для кнопки
   */
  const removeButtonErrors = useCallback((index) => {
    setButtonErrors((prevErrors) => {
      const newErrors = {};
      Object.keys(prevErrors).forEach((key) => {
        const [errorIndex, field] = key.split('_');
        const errorIndexNum = parseInt(errorIndex);

        if (errorIndexNum < index) {
          // Индексы меньше удаляемого остаются без изменений
          newErrors[key] = prevErrors[key];
        } else if (errorIndexNum > index) {
          // Индексы больше удаляемого уменьшаются на 1
          newErrors[`${errorIndexNum - 1}_${field}`] = prevErrors[key];
        }
        // Ошибки для удаляемого индекса просто не добавляются
      });
      return newErrors;
    });
  }, []);

  /**
   * Добавление ошибки для новой кнопки
   */
  const addButtonError = useCallback((index) => {
    setButtonErrors((prevErrors) => ({
      ...prevErrors,
      [`${index}_link`]: 'Ссылка обязательна для заполнения'
    }));
  }, []);

  /**
   * Полная валидация формы
   */
  const validateFullForm = useCallback((formData) => {
    const {
      formErrors: newFormErrors,
      buttonErrors: newButtonErrors,
      dateErrors: newDateErrors
    } = validatePromotionForm(formData, mode);

    setFormErrors(newFormErrors);
    setButtonErrors(newButtonErrors);

    if (mode !== 'edit') {
      setDateErrors(newDateErrors);
    }

    const hasErrors =
      Object.keys(newFormErrors).length > 0 ||
      Object.keys(newButtonErrors).length > 0 ||
      (mode === 'edit' ? false : Object.keys(newDateErrors).length > 0)

    return !hasErrors;
  }, [mode]);

  /**
   * Очистка всех ошибок
   */
  const clearAllErrors = useCallback(() => {
    setButtonErrors({});
    setDateErrors({});
    setFormErrors({});
  }, []);

  return {
    // Состояния ошибок
    buttonErrors,
    dateErrors,
    formErrors,

    // Функции валидации
    validateButtonLink,
    validateFormDates,
    validateFullForm,

    // Управление ошибками кнопок
    removeButtonErrors,
    addButtonError,

    // Утилиты
    clearAllErrors
  };
};
